import React, { useState } from 'react';
import { 
  Shield, 
  Plus, 
  Edit, 
  Trash2, 
  Users, 
  Key, 
  Search,
  RefreshCw,
  X,
  Check,
  Copy,
  Layers,
  TrendingUp,
  ChevronDown,
  ChevronRight
} from 'lucide-react';
import { 
  ROLE_TEMPLATES,
  PERMISSION_GROUPS,
  getRoleCategoryColor,
  getPermissionActionColor
} from '../../shared/types/role-management';

// Mock data for demo
const mockRoles = [
  {
    id: '1',
    name: 'admin',
    description: 'Full system administration access',
    is_active: true,
    created_at: '2025-01-01T00:00:00Z',
    permissions: [
      { id: '1', name: 'users:manage', action: 'manage', resource: 'users', description: 'Manage user accounts' },
      { id: '2', name: 'roles:manage', action: 'manage', resource: 'roles', description: 'Manage roles and permissions' },
      { id: '3', name: 'campaigns:manage', action: 'manage', resource: 'campaigns', description: 'Manage marketing campaigns' }
    ]
  },
  {
    id: '2',
    name: 'campaign_manager',
    description: 'Marketing campaign management',
    is_active: true,
    created_at: '2025-01-02T00:00:00Z',
    permissions: [
      { id: '3', name: 'campaigns:manage', action: 'manage', resource: 'campaigns', description: 'Manage marketing campaigns' },
      { id: '4', name: 'artists:read', action: 'read', resource: 'artists', description: 'View artist information' },
      { id: '5', name: 'reports:read', action: 'read', resource: 'reports', description: 'View reports and analytics' }
    ]
  },
  {
    id: '3',
    name: 'artist_manager',
    description: 'Artist and release management',
    is_active: true,
    created_at: '2025-01-03T00:00:00Z',
    permissions: [
      { id: '6', name: 'artists:manage', action: 'manage', resource: 'artists', description: 'Manage artist profiles' },
      { id: '7', name: 'releases:manage', action: 'manage', resource: 'releases', description: 'Manage music releases' },
      { id: '4', name: 'artists:read', action: 'read', resource: 'artists', description: 'View artist information' }
    ]
  }
];

const mockAnalytics = {
  total_roles: 8,
  active_roles: 7,
  permission_coverage: 85,
  security_score: 92
};

const mockPermissions = [
  { id: '1', name: 'users:manage', action: 'manage', resource: 'users', description: 'Manage user accounts' },
  { id: '2', name: 'roles:manage', action: 'manage', resource: 'roles', description: 'Manage roles and permissions' },
  { id: '3', name: 'campaigns:manage', action: 'manage', resource: 'campaigns', description: 'Manage marketing campaigns' },
  { id: '4', name: 'artists:read', action: 'read', resource: 'artists', description: 'View artist information' },
  { id: '5', name: 'reports:read', action: 'read', resource: 'reports', description: 'View reports and analytics' },
  { id: '6', name: 'artists:manage', action: 'manage', resource: 'artists', description: 'Manage artist profiles' },
  { id: '7', name: 'releases:manage', action: 'manage', resource: 'releases', description: 'Manage music releases' },
  { id: '8', name: 'budgets:read', action: 'read', resource: 'budgets', description: 'View budget information' },
  { id: '9', name: 'campaigns:create', action: 'create', resource: 'campaigns', description: 'Create new campaigns' },
  { id: '10', name: 'artists:create', action: 'create', resource: 'artists', description: 'Add new artists' }
];

// Role Statistics Component
const RoleStatistics = () => (
  <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center">
        <div className="flex-shrink-0">
          <Shield className="h-8 w-8 text-blue-600" />
        </div>
        <div className="ml-4">
          <p className="text-sm font-medium text-gray-500">Total Roles</p>
          <p className="text-2xl font-semibold text-gray-900">{mockAnalytics.total_roles}</p>
        </div>
      </div>
    </div>
    
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center">
        <div className="flex-shrink-0">
          <Users className="h-8 w-8 text-green-600" />
        </div>
        <div className="ml-4">
          <p className="text-sm font-medium text-gray-500">Active Roles</p>
          <p className="text-2xl font-semibold text-gray-900">{mockAnalytics.active_roles}</p>
        </div>
      </div>
    </div>
    
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center">
        <div className="flex-shrink-0">
          <Key className="h-8 w-8 text-purple-600" />
        </div>
        <div className="ml-4">
          <p className="text-sm font-medium text-gray-500">Permission Coverage</p>
          <p className="text-2xl font-semibold text-gray-900">{mockAnalytics.permission_coverage}%</p>
        </div>
      </div>
    </div>
    
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center">
        <div className="flex-shrink-0">
          <TrendingUp className="h-8 w-8 text-yellow-600" />
        </div>
        <div className="ml-4">
          <p className="text-sm font-medium text-gray-500">Security Score</p>
          <p className="text-2xl font-semibold text-gray-900">{mockAnalytics.security_score}/100</p>
        </div>
      </div>
    </div>
  </div>
);

// Role Templates Component
const RoleTemplatesSection = () => {
  const [showTemplates, setShowTemplates] = useState(true);
  const templates = Object.values(ROLE_TEMPLATES);

  return (
    <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center">
          <Layers className="h-5 w-5 text-blue-600 mr-2" />
          <h3 className="text-lg font-medium text-gray-900">Role Templates</h3>
        </div>
        <button
          onClick={() => setShowTemplates(!showTemplates)}
          className="flex items-center text-sm text-blue-600 hover:text-blue-800"
        >
          {showTemplates ? <ChevronDown className="h-4 w-4 mr-1" /> : <ChevronRight className="h-4 w-4 mr-1" />}
          {showTemplates ? 'Hide' : 'Show'} Templates
        </button>
      </div>

      {showTemplates && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {templates.filter(t => t.is_default).map((template, index) => (
            <div
              key={index}
              className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors"
            >
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center">
                  <span className="text-2xl mr-2">{template.icon}</span>
                  <div>
                    <h4 className="text-sm font-medium text-gray-900">{template.name}</h4>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getRoleCategoryColor(template.category)}`}>
                      {template.category.replace('_', ' ')}
                    </span>
                  </div>
                </div>
              </div>
              
              <p className="text-xs text-gray-600 mb-3">{template.description}</p>
              
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-500">
                  {template.permissions.length} permissions
                </span>
                <button className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-blue-700 bg-blue-100 hover:bg-blue-200">
                  <Plus className="h-3 w-3 mr-1" />
                  Use Template
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Enhanced Permission Matrix Component
const EnhancedPermissionMatrix = ({ role }: { role: any }) => {
  const [expandedGroups, setExpandedGroups] = useState(new Set(['artist_management', 'campaign_operations']));
  const [searchTerm, setSearchTerm] = useState('');
  const permissionGroups = Object.values(PERMISSION_GROUPS).sort((a, b) => a.order - b.order);

  const toggleGroup = (groupId: string) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(groupId)) {
      newExpanded.delete(groupId);
    } else {
      newExpanded.add(groupId);
    }
    setExpandedGroups(newExpanded);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-gray-900">
          Permissions for {role.name}
        </h3>
        <div className="text-sm text-gray-500">
          {role.permissions.length} of {mockPermissions.length} permissions granted
        </div>
      </div>

      {/* Permission Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
        <input
          type="text"
          placeholder="Search permissions..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
      </div>

      {/* Permission Groups */}
      <div className="space-y-4">
        {permissionGroups.slice(0, 3).map((group) => {
          const isExpanded = expandedGroups.has(group.id);
          const groupPermissions = mockPermissions.filter(p => 
            group.permissions.some(gp => p.name.includes(gp.split(':')[0]))
          );
          const grantedCount = groupPermissions.filter(p => 
            role.permissions.some((rp: any) => rp.id === p.id)
          ).length;

          return (
            <div key={group.id} className="border border-gray-200 rounded-lg">
              <div
                className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50"
                onClick={() => toggleGroup(group.id)}
              >
                <div className="flex items-center">
                  <span className="text-2xl mr-3">{group.icon}</span>
                  <div>
                    <h4 className="text-sm font-medium text-gray-900">{group.name}</h4>
                    <p className="text-xs text-gray-500">{group.description}</p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-3">
                  <span className="text-sm text-gray-500">
                    {grantedCount}/{groupPermissions.length} granted
                  </span>
                  <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div 
                      className={`h-full bg-${group.color}-500 transition-all duration-300`}
                      style={{ width: `${(grantedCount / groupPermissions.length) * 100}%` }}
                    />
                  </div>
                  {isExpanded ? (
                    <ChevronDown className="h-4 w-4 text-gray-400" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-gray-400" />
                  )}
                </div>
              </div>

              {isExpanded && (
                <div className="border-t border-gray-200 p-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {groupPermissions.map((permission) => {
                      const isGranted = role.permissions.some((p: any) => p.id === permission.id);
                      return (
                        <label
                          key={permission.id}
                          className={`relative flex items-center p-3 rounded-lg border cursor-pointer transition-colors ${
                            isGranted 
                              ? 'bg-blue-50 border-blue-200' 
                              : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                          }`}
                        >
                          <div className="flex items-center w-full">
                            <div className={`flex-shrink-0 w-4 h-4 rounded border-2 flex items-center justify-center mr-3 ${
                              isGranted 
                                ? 'bg-blue-600 border-blue-600' 
                                : 'border-gray-300'
                            }`}>
                              {isGranted && <Check className="w-3 h-3 text-white" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className={`text-xs font-medium ${getPermissionActionColor(permission.action)} px-2 py-1 rounded-full inline-block mb-1`}>
                                {permission.action}
                              </div>
                              <div className="text-xs text-gray-600 truncate">
                                {permission.resource}
                              </div>
                              <div className="text-xs text-gray-500 mt-1 truncate">
                                {permission.description}
                              </div>
                            </div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Main Demo Component
export const EnhancedRolesDemo: React.FC = () => {
  const [selectedRole, setSelectedRole] = useState(mockRoles[0]);
  const [searchTerm, setSearchTerm] = useState('');

  const filteredRoles = mockRoles.filter(role =>
    role.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    role.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getRoleColor = (roleName: string) => {
    switch (roleName) {
      case 'admin': return 'bg-red-100 text-red-800 border-red-200';
      case 'campaign_manager': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'artist_manager': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center mb-4">
            <Shield className="h-8 w-8 text-blue-600 mr-3" />
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Enhanced Role Management Demo</h1>
            </div>
          </div>
          
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Shield className="h-5 w-5 text-blue-600" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-blue-800">Demo Mode</h3>
              </div>
            </div>
          </div>
        </div>

        {/* Role Statistics */}
        <RoleStatistics />

        {/* Role Templates */}
        <RoleTemplatesSection />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Roles List */}
          <div className="space-y-4">
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Roles</h3>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search roles..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              <div className="space-y-2">
                {filteredRoles.map((role) => (
                  <div
                    key={role.id}
                    className={`p-4 rounded-lg border cursor-pointer transition-colors ${
                      selectedRole?.id === role.id
                        ? 'bg-blue-50 border-blue-200'
                        : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                    }`}
                    onClick={() => setSelectedRole(role)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getRoleColor(role.name)}`}>
                            {role.name}
                          </span>
                        </div>
                        <p className="mt-1 text-sm text-gray-600">{role.description}</p>
                        <p className="text-xs text-gray-500">
                          {role.permissions.length} permissions assigned
                        </p>
                      </div>
                      
                      <div className="flex items-center space-x-1 ml-4">
                        <button className="p-1 text-gray-400 hover:text-blue-600" title="Edit role">
                          <Edit className="h-4 w-4" />
                        </button>
                        <button className="p-1 text-gray-400 hover:text-green-600" title="Clone role">
                          <Copy className="h-4 w-4" />
                        </button>
                        <button className="p-1 text-gray-400 hover:text-red-600" title="Delete role">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Role Details & Permissions */}
          <div className="space-y-4">
            <div className="bg-white rounded-lg shadow-sm p-6">
              <EnhancedPermissionMatrix role={selectedRole} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnhancedRolesDemo;
